import { BrowserAuthError, BrowserAuthErrorCodes, InteractionRequiredAuthError } from '@azure/msal-browser';

/**
 * How a failed `acquireTokenSilent` should be treated.
 *
 * - `dead` — the session cannot be renewed without the user. Sign out to the sign-in page.
 * - `transient` — maybe a network blip or an Entra 5xx. Fail this request, ask again later.
 *
 * Kept apart from `session.ts` (which imports the MSAL instance, and with it `window`) so the rule
 * is testable on its own.
 */
export type SilentFailure = 'dead' | 'transient';

/**
 * `BrowserAuthError` codes that fail the same way on every attempt. Retrying one never succeeds, so
 * treating it as transient is an infinite 401 loop with the user stuck on a page that never loads.
 *
 * `block_iframe_reload`: the renewal iframe loaded a page that runs MSAL. Configuration, not
 * weather. It is what every renewal hit while `redirectUri` pointed silent calls at the app root.
 *
 * `timed_out` is deliberately NOT here: a slow network produces it too. The streak cap in
 * `session.ts` catches it when it is persistent.
 */
const DETERMINISTIC_BROWSER_CODES: ReadonlySet<string> = new Set([
  BrowserAuthErrorCodes.blockIframeReload,
]);

/**
 * What the caller knows beyond the error itself. Optional so the error-only rule stays testable.
 *
 * - `tokenExpired`: the cached id token is already past its `exp`.
 * - `online`: `navigator.onLine`.
 */
export interface SilentFailureContext {
  tokenExpired: boolean;
  online: boolean;
}

/**
 * `ctx` adds one rule: a failure we would otherwise call transient is **dead** when the token is
 * already past `exp` and the browser is online. A timeout cannot rescue a token we know has died,
 * and waiting out three of them (each a full iframe wait) is the 30 seconds users sat through
 * before the sign-in page. Offline stays transient: that is the blip the streak exists to forgive.
 */
export function classifySilentFailure(e: unknown, ctx?: SilentFailureContext): SilentFailure {
  if (e instanceof InteractionRequiredAuthError) return 'dead';
  if (e instanceof BrowserAuthError && DETERMINISTIC_BROWSER_CODES.has(e.errorCode)) return 'dead';
  if (ctx?.tokenExpired && ctx.online) return 'dead';
  return 'transient';
}

/**
 * Consecutive transient failures, with no success between them, after which the session is treated
 * as dead anyway.
 *
 * The backstop for an error that is deterministic but not on the list above. Without it, the only
 * way out of a misclassified failure is the user closing the tab. Three attempts span a few minutes
 * of the health probe's 60s ticks, or one burst of 401s, and a real blip rarely survives that.
 */
export const MAX_TRANSIENT_STREAK = 3;

/** Counts consecutive transient failures. `success` resets it. */
export class TransientStreak {
  private count = 0;

  /** Record a transient failure. True when the streak has reached the cap. */
  fail(): boolean {
    this.count += 1;
    return this.count >= MAX_TRANSIENT_STREAK;
  }

  success(): void {
    this.count = 0;
  }
}
